import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Heart, Brain, Sparkles } from 'lucide-react';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useState } from 'react';

const translations = {
  de: {
    title: 'Neurodiversität & Asperger-Autismus',
    description: 'Informationen über Neurodiversität, Asperger-Autismus und wichtige Awareness-Tage',
    hero: {
      title: 'Neurodiversität & Asperger-Autismus',
      subtitle: 'Vielfalt des Denkens als Stärke',
    },
    awareness: {
      title: 'Wichtige Awareness-Tage',
      autism: {
        title: 'Welt-Autismus-Tag',
        date: '2. April',
        description:
          'Der Welt-Autismus-Tag wurde von den Vereinten Nationen ins Leben gerufen, um das Bewusstsein für Autismus zu schärfen und die Rechte autistischer Menschen zu stärken.',
        color: 'blue',
      },
      neurodiversity: {
        title: 'Neurodiversity Pride Day',
        date: '16. Juni',
        description:
          'Der Neurodiversity Pride Day feiert die Vielfalt neurologischer Unterschiede und fördert Akzeptanz und Verständnis für alle neurodiversen Menschen.',
        color: 'rainbow',
      },
      autistic: {
        title: 'Autistic Pride Day',
        date: '18. Juni',
        description:
          'Der Autistic Pride Day feiert autistische Kultur und Identität. Das Symbol des Unendlichkeitszeichens (∞) in Regenbogenfarben steht für die unendliche Vielfalt des Autismus-Spektrums.',
        color: 'rainbow',
      },
    },
    asperger: {
      title: 'Was ist Asperger-Autismus?',
      intro:
        'Asperger-Autismus ist eine Form des Autismus-Spektrums, die durch besondere Stärken und Herausforderungen gekennzeichnet ist.',
      characteristics: {
        title: 'Charakteristika',
        items: [
          'Intensive Spezialinteressen und tiefes Fachwissen in bestimmten Bereichen',
          'Detailorientiertes, analytisches Denken',
          'Ehrlichkeit und direkte Kommunikation',
          'Besondere Wahrnehmung von Details und Mustern',
          'Herausforderungen in der sozialen Kommunikation',
          'Bedürfnis nach Strukturen und Routinen',
        ],
      },
      strengths: {
        title: 'Stärken',
        items: [
          'Außergewöhnliche Konzentrationsfähigkeit bei Interessengebieten',
          'Logisches und systematisches Denken',
          'Kreativität und innovative Problemlösungen',
          'Hohe Zuverlässigkeit und Genauigkeit',
          'Authentizität und Integrität',
        ],
      },
    },
    neurodiversity: {
      title: 'Das Neurodiversitäts-Paradigma',
      intro:
        'Neurodiversität ist ein Konzept, das neurologische Unterschiede als natürliche Variationen des menschlichen Genoms betrachtet.',
      principles: {
        title: 'Grundprinzipien',
        items: [
          'Neurologische Unterschiede sind natürliche Variationen, keine Defizite',
          'Neurodiversität bereichert unsere Gesellschaft',
          'Jeder Mensch hat einzigartige Stärken und Herausforderungen',
          'Inklusion und Akzeptanz statt Anpassung und "Heilung"',
          'Selbstbestimmung und Autonomie für neurodivergente Menschen',
        ],
      },
      includes: {
        title: 'Neurodiversität umfasst u.a.',
        items: [
          'Autismus-Spektrum (inkl. Asperger)',
          'ADHS',
          'Legasthenie und Dyskalkulie',
          'Hochsensibilität',
          'Hochbegabung',
          'Und viele weitere neurologische Variationen',
        ],
      },
    },
    tech: {
      title: 'Autismus & Technologie',
      intro:
        'Viele autistische Menschen haben eine besondere Affinität zu Technologie und tragen wesentlich zur Innovation bei.',
      areas: [
        {
          title: 'Programmierung & Softwareentwicklung',
          description:
            'Systematisches Denken und Liebe zum Detail sind perfekt für Code',
        },
        {
          title: 'Dezentrale Technologien',
          description:
            'Bitcoin, Lightning, Nostr - Systeme, die auf Logik und Mathematik basieren',
        },
        {
          title: 'Open Source',
          description: 'Transparenz, Dokumentation und klare Strukturen',
        },
        {
          title: 'Kryptographie',
          description: 'Komplexe mathematische Systeme und Mustererkennung',
        },
      ],
    },
    resources: {
      title: 'Ressourcen & Informationen',
      intro: 'Hilfreiche Organisationen und Informationsquellen:',
      links: [
        {
          title: 'Autismus Deutschland e.V.',
          url: 'https://www.autismus.de/',
          description: 'Bundesverband zur Förderung von Menschen mit Autismus',
        },
        {
          title: 'Aspies e.V.',
          url: 'https://www.aspies.de/',
          description: 'Selbsthilfeorganisation von und für Menschen im Autismus-Spektrum',
        },
        {
          title: 'Neurodiversity Hub',
          url: 'https://neurodiversityhub.org/',
          description: 'Internationale Ressourcen zum Neurodiversitäts-Paradigma',
        },
      ],
    },
    footer: {
      back: 'Zurück zur Startseite',
    },
  },
  en: {
    title: 'Neurodiversity & Asperger Autism',
    description: 'Information about neurodiversity, Asperger autism and important awareness days',
    hero: {
      title: 'Neurodiversity & Asperger Autism',
      subtitle: 'Diversity of Thinking as Strength',
    },
    awareness: {
      title: 'Important Awareness Days',
      autism: {
        title: 'World Autism Awareness Day',
        date: 'April 2nd',
        description:
          'World Autism Awareness Day was established by the United Nations to raise awareness about autism and strengthen the rights of autistic people.',
        color: 'blue',
      },
      neurodiversity: {
        title: 'Neurodiversity Pride Day',
        date: 'June 16th',
        description:
          'Neurodiversity Pride Day celebrates the diversity of neurological differences and promotes acceptance and understanding for all neurodiverse people.',
        color: 'rainbow',
      },
      autistic: {
        title: 'Autistic Pride Day',
        date: 'June 18th',
        description:
          'Autistic Pride Day celebrates autistic culture and identity. The infinity symbol (∞) in rainbow colors represents the infinite diversity of the autism spectrum.',
        color: 'rainbow',
      },
    },
    asperger: {
      title: 'What is Asperger Autism?',
      intro:
        'Asperger autism is a form of the autism spectrum characterized by unique strengths and challenges.',
      characteristics: {
        title: 'Characteristics',
        items: [
          'Intense special interests and deep expertise in specific areas',
          'Detail-oriented, analytical thinking',
          'Honesty and direct communication',
          'Unique perception of details and patterns',
          'Challenges in social communication',
          'Need for structures and routines',
        ],
      },
      strengths: {
        title: 'Strengths',
        items: [
          'Exceptional ability to focus on areas of interest',
          'Logical and systematic thinking',
          'Creativity and innovative problem-solving',
          'High reliability and accuracy',
          'Authenticity and integrity',
        ],
      },
    },
    neurodiversity: {
      title: 'The Neurodiversity Paradigm',
      intro:
        'Neurodiversity is a concept that views neurological differences as natural variations of the human genome.',
      principles: {
        title: 'Core Principles',
        items: [
          'Neurological differences are natural variations, not deficits',
          'Neurodiversity enriches our society',
          'Every person has unique strengths and challenges',
          'Inclusion and acceptance instead of adaptation and "cure"',
          'Self-determination and autonomy for neurodivergent people',
        ],
      },
      includes: {
        title: 'Neurodiversity includes',
        items: [
          'Autism Spectrum (incl. Asperger)',
          'ADHD',
          'Dyslexia and Dyscalculia',
          'High Sensitivity',
          'Giftedness',
          'And many other neurological variations',
        ],
      },
    },
    tech: {
      title: 'Autism & Technology',
      intro:
        'Many autistic people have a special affinity for technology and contribute significantly to innovation.',
      areas: [
        {
          title: 'Programming & Software Development',
          description: 'Systematic thinking and attention to detail are perfect for code',
        },
        {
          title: 'Decentralized Technologies',
          description: 'Bitcoin, Lightning, Nostr - systems based on logic and mathematics',
        },
        {
          title: 'Open Source',
          description: 'Transparency, documentation and clear structures',
        },
        {
          title: 'Cryptography',
          description: 'Complex mathematical systems and pattern recognition',
        },
      ],
    },
    resources: {
      title: 'Resources & Information',
      intro: 'Helpful organizations and information sources:',
      links: [
        {
          title: 'Autism Speaks',
          url: 'https://www.autismspeaks.org/',
          description: 'Global autism advocacy organization',
        },
        {
          title: 'ASAN (Autistic Self Advocacy Network)',
          url: 'https://autisticadvocacy.org/',
          description: 'Organization run by and for autistic people',
        },
        {
          title: 'Neurodiversity Hub',
          url: 'https://neurodiversityhub.org/',
          description: 'International resources on the neurodiversity paradigm',
        },
      ],
    },
    footer: {
      back: 'Back to Home',
    },
  },
};

const NeurodiversityPage = () => {
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const t = translations[language];

  useSeoMeta({
    title: t.title,
    description: t.description,
    ogTitle: t.title,
    ogDescription: t.description,
    ogImage: 'https://stl1988.shakespeare.wtf/og-image.jpg',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container py-8 px-4 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.footer.back}
            </Link>
          </Button>
          
          <LanguageToggle
            language={language}
            onToggle={() => setLanguage(language === 'de' ? 'en' : 'de')}
          />
        </div>

        {/* Hero */}
        <div className="text-center space-y-4 mb-12">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
              <Brain className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold">
            {t.hero.title}
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground">{t.hero.subtitle}</p>
        </div>

        {/* Awareness Days */}
        <section className="mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6 text-center">
            {t.awareness.title}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* World Autism Day */}
            <Card className="border-2 border-blue-500/30 hover:border-blue-500/50 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-500" />
                  </div>
                  <Badge variant="secondary" className="bg-blue-500/20 text-blue-700 dark:text-blue-400">
                    {t.awareness.autism.date}
                  </Badge>
                </div>
                <CardTitle className="text-xl">{t.awareness.autism.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{t.awareness.autism.description}</p>
              </CardContent>
            </Card>

            {/* Neurodiversity Pride Day */}
            <Card className="border-2 border-purple-500/30 hover:border-purple-500/50 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <Badge variant="secondary" className="bg-purple-500/20 text-purple-700 dark:text-purple-400">
                    {t.awareness.neurodiversity.date}
                  </Badge>
                </div>
                <CardTitle className="text-xl">{t.awareness.neurodiversity.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {t.awareness.neurodiversity.description}
                </p>
              </CardContent>
            </Card>

            {/* Autistic Pride Day */}
            <Card className="border-2 border-pink-500/30 hover:border-pink-500/50 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400 flex items-center justify-center">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <Badge variant="secondary" className="bg-pink-500/20 text-pink-700 dark:text-pink-400">
                    {t.awareness.autistic.date}
                  </Badge>
                </div>
                <CardTitle className="text-xl">{t.awareness.autistic.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {t.awareness.autistic.description}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Asperger Autism */}
        <section className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl sm:text-3xl">{t.asperger.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-base leading-relaxed">{t.asperger.intro}</p>

              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold">{t.asperger.characteristics.title}</h3>
                <ul className="space-y-2">
                  {t.asperger.characteristics.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold">{t.asperger.strengths.title}</h3>
                <ul className="space-y-2">
                  {t.asperger.strengths.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">✓</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Neurodiversity Paradigm */}
        <section className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl sm:text-3xl">{t.neurodiversity.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-base leading-relaxed">{t.neurodiversity.intro}</p>

              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold">
                  {t.neurodiversity.principles.title}
                </h3>
                <ul className="space-y-2">
                  {t.neurodiversity.principles.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold">
                  {t.neurodiversity.includes.title}
                </h3>
                <ul className="space-y-2">
                  {t.neurodiversity.includes.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Autism & Technology */}
        <section className="mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6">{t.tech.title}</h2>
          <p className="text-base text-muted-foreground mb-6">{t.tech.intro}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {t.tech.areas.map((area, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{area.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl sm:text-3xl">{t.resources.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-base text-muted-foreground">{t.resources.intro}</p>
              <div className="space-y-3">
                {t.resources.links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 rounded-lg border hover:bg-muted transition-colors"
                  >
                    <h4 className="font-semibold text-primary hover:underline">{link.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default NeurodiversityPage;
